import {
	ADD_COMPANY_HOTEL_COMPETITOR_TAG_TO_COMPETITOR_MUTATION,
	AUTO_UPDATE_COMPANY_HOTEL_ROOM_MAPPING_TYPES_MUTATION,
	CREATE_COMPANY_HOTEL_COMPETITOR_TAG_MUTATION,
	CREATE_COMPANY_HOTEL_MUTATION,
	DELETE_COMPANY_HOTEL_COMPETITOR_TAG_MUTATION,
	DISABLE_COMPANY_HOTEL_MUTATION,
	DISABLE_COMPANY_HOTEL_ROOM_MAPPING_TYPE_CONFIG_MUTATION,
	EDIT_COMPANY_HOTEL_COMPETITOR_TAG_MUTATION,
	ENRICH_HOTEL_ROOMS_MUTATION,
	ENABLE_COMPANY_HOTEL_DATA_PROVIDER_MUTATION,
	FIND_COMPANY_HOTEL_WITH_COMPETITORS_QUERY,
	FIND_MY_COMPANY_HOTELS_LITE_QUERY,
	FIND_MY_COMPANY_HOTELS_WITH_COMPETITORS_QUERY,
	REMOVE_COMPANY_HOTEL_COMPETITOR_TAG_FROM_COMPETITOR_MUTATION,
	REMOVE_COMPANY_HOTEL_COMPETITORS_GROUP_MUTATION,
	REORDER_COMPANY_HOTEL_COMPETITORS_MUTATION,
	SANITIZE_ROOM_MAPPINGS_MUTATION,
	SAVE_COMPANY_HOTEL_METARADAR_SETTINGS_MUTATION,
	SAVE_COMPANY_HOTEL_MUTATION,
	SAVE_COMPANY_HOTEL_PARSING_SETTING_MUTATION,
	SAVE_COMPANY_HOTEL_ROOM_MAPPING_TYPE_CONFIG_MUTATION,
	SAVE_OR_CREATE_COMPETITORS_GROUP_MUTATION,
	UPDATE_COMPANY_HOTEL_ROOM_MAPPING_TYPE_MUTATION,
	UPDATE_COMPANY_HOTEL_SUBSCRIPTION_LAST_LOGIN_DATE_MUTATION,
} from '@app/model/company_hotel/service/CompanyHotelGraphQLQueries';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import CompanyHotelInput from '@app/model/company_hotel/input/CompanyHotelInput';
import { GraphQLExecutor } from '@app/model/_common/service/GraphQLExecutor';
import CompanyHotelParsingSettingInput from '@app/model/company_hotel/input/CompanyHotelParsingSettingInput';
import DataProvider from '@app/model/_common/DataProvider';
import { CompanyHotelCompetitorsOrderInput } from '@app/model/company_hotel/input/CompanyHotelCompetitorsOrderInput';
import HotelRoomMappingType from '@app/model/market_state/HotelRoomMappingType';
import Return from '@app/model/_common/Return';
import CompanyHotelMetaradarSettingsInput from '@app/model/company_hotel/input/CompanyHotelMetaradarSettingsInput';
import SaveOrCreateCompetitorsGroupInput from '@app/model/company_hotel/input/SaveOrCreateCompetitorsGroupInput';
import CompanyHotelLite from '@app/model/company_hotel/CompanyHotelLite';
import CompanyHotelMutationResult from '@app/model/company_hotel/CompanyHotelMutationResult';
import ProcessRequestExecutionState from '@app/model/process-request/ProcessRequestExecutionState';

class CompanyHotelService {
	public static async findCompanyHotelWithCompetitors(companyHotelId: number): Promise<CompanyHotel> {
		const result = await GraphQLExecutor.execute<CompanyHotel>(FIND_COMPANY_HOTEL_WITH_COMPETITORS_QUERY, { companyHotelId });
		return new CompanyHotel(result);
	}

	public static async findCompanyHotels(): Promise<CompanyHotel[]> {
		const results = await GraphQLExecutor.execute<CompanyHotel[]>(FIND_MY_COMPANY_HOTELS_WITH_COMPETITORS_QUERY);
		return results.map(item => new CompanyHotel(item));
	}

	public static async findCompanyHotelsLite(): Promise<CompanyHotelLite[]> {
		return GraphQLExecutor.execute<CompanyHotelLite[]>(FIND_MY_COMPANY_HOTELS_LITE_QUERY);
	}

	public static async saveCompanyHotel(input: CompanyHotelInput): Promise<CompanyHotelMutationResult> {
		const result = await GraphQLExecutor.execute<CompanyHotelMutationResult>(SAVE_COMPANY_HOTEL_MUTATION, { input });
		return new CompanyHotelMutationResult(result);
	}

	public static async removeCompanyHotelCompetitorsGroup(variables: {
		companyHotelId: number,
		competitorsGroupId: number
	}): Promise<CompanyHotel> {
		const result = await GraphQLExecutor.execute<CompanyHotel>(REMOVE_COMPANY_HOTEL_COMPETITORS_GROUP_MUTATION, variables);
		return new CompanyHotel(result);
	}

	public static async createCompanyHotel(input: CompanyHotelInput): Promise<CompanyHotelMutationResult> {
		const result = await GraphQLExecutor.execute<CompanyHotelMutationResult>(CREATE_COMPANY_HOTEL_MUTATION, { input });
		return new CompanyHotelMutationResult(result);
	}

	public static async disableCompanyHotel(companyHotelId: number): Promise<CompanyHotel[]> {
		const results = await GraphQLExecutor.execute<CompanyHotel[]>(DISABLE_COMPANY_HOTEL_MUTATION, { companyHotelId });
		return results.map(item => new CompanyHotel(item));
	}

	public static updateCompanyHotelSubscriptionLastLoginDate(variables: { companyHotelId: number }): Promise<Return> {
		return GraphQLExecutor.execute<Return>(UPDATE_COMPANY_HOTEL_SUBSCRIPTION_LAST_LOGIN_DATE_MUTATION, variables);
	}

	public static async saveCompanyMetaradarHotelSettings(input: {
		companyHotelId: number,
		input: CompanyHotelMetaradarSettingsInput,
	}): Promise<CompanyHotel> {
		const result = await GraphQLExecutor.execute<CompanyHotel>(SAVE_COMPANY_HOTEL_METARADAR_SETTINGS_MUTATION, input);
		return new CompanyHotel(result);
	}

	public static async saveCompanyHotelParsingSetting(input: {
		companyHotelId: number,
		input: CompanyHotelParsingSettingInput
	}): Promise<CompanyHotel> {
		const result = await GraphQLExecutor.execute<CompanyHotel>(SAVE_COMPANY_HOTEL_PARSING_SETTING_MUTATION, input);
		return new CompanyHotel(result);
	}

	public static async enableCompanyHotelDataProvider(input: {
		companyHotelId: number,
		provider: DataProvider
	}): Promise<CompanyHotel> {
		const result = await GraphQLExecutor.execute<CompanyHotel>(ENABLE_COMPANY_HOTEL_DATA_PROVIDER_MUTATION, input);
		return new CompanyHotel(result);
	}

	public static async reorderCompanyHotelCompetitors(input: CompanyHotelCompetitorsOrderInput): Promise<Return> {
		return GraphQLExecutor.execute<Return>(REORDER_COMPANY_HOTEL_COMPETITORS_MUTATION, { input: input });
	}

	public static async updateCompanyHotelRoomMappingType(input: {
		companyHotelId: number,
		hotelId: number,
		roomId: number,
		type: HotelRoomMappingType
	}): Promise<CompanyHotel> {
		const result = await GraphQLExecutor.execute<CompanyHotel>(UPDATE_COMPANY_HOTEL_ROOM_MAPPING_TYPE_MUTATION, { ...input });
		return new CompanyHotel(result);
	}

	public static async autoUpdateCompanyHotelRoomMappingTypes(input: { companyHotelId: number, hotelId: number }): Promise<CompanyHotel> {
		const result = await GraphQLExecutor.execute<CompanyHotel>(AUTO_UPDATE_COMPANY_HOTEL_ROOM_MAPPING_TYPES_MUTATION, { ...input });
		return new CompanyHotel(result);
	}

	public static async enrichHotelRooms(input: { companyHotelId: number, hotelId: number }): Promise<ProcessRequestExecutionState[]> {
		const result = await GraphQLExecutor.execute<ProcessRequestExecutionState[]>(ENRICH_HOTEL_ROOMS_MUTATION, input);
		return result.map(execution => new ProcessRequestExecutionState(execution));
	}

	public static async saveCompanyHotelRoomMappingTypeConfig(variables: {
		companyHotelId: number,
		type: HotelRoomMappingType,
		name: string

	}) {
		const result = await GraphQLExecutor.execute<CompanyHotel>(SAVE_COMPANY_HOTEL_ROOM_MAPPING_TYPE_CONFIG_MUTATION, variables);
		return new CompanyHotel(result);
	}

	public static async disableCompanyHotelRoomMappingTypeConfig(variables: {
		companyHotelId: number,
		type: HotelRoomMappingType
	}) {
		const result = await GraphQLExecutor.execute<CompanyHotel>(DISABLE_COMPANY_HOTEL_ROOM_MAPPING_TYPE_CONFIG_MUTATION, variables);
		return new CompanyHotel(result);
	}

	public static async createCompanyHotelCompetitorTag(variables: {
		companyHotelId: number,
		name: string,
		color: string
	}) {
		const result = await GraphQLExecutor.execute<CompanyHotel>(CREATE_COMPANY_HOTEL_COMPETITOR_TAG_MUTATION, variables);
		return new CompanyHotel(result);
	}

	public static async editCompanyHotelCompetitorTag(variables: {
		companyHotelId: number,
		competitorTagId: number,
		name: string,
		color: string
	}) {
		const result = await GraphQLExecutor.execute<CompanyHotel>(EDIT_COMPANY_HOTEL_COMPETITOR_TAG_MUTATION, variables);
		return new CompanyHotel(result);
	}

	public static async deleteCompanyHotelCompetitorTag(variables: {
		companyHotelId: number,
		competitorTagId: number
	}) {
		const result = await GraphQLExecutor.execute<CompanyHotel>(DELETE_COMPANY_HOTEL_COMPETITOR_TAG_MUTATION, variables);
		return new CompanyHotel(result);
	}

	public static async addCompanyHotelCompetitorTagToCompetitor(variables: {
		companyHotelId: number,
		competitorId: number,
		competitorTagId: number
	}) {
		const result = await GraphQLExecutor.execute<CompanyHotel>(ADD_COMPANY_HOTEL_COMPETITOR_TAG_TO_COMPETITOR_MUTATION, variables);
		return new CompanyHotel(result);
	}

	public static async removeCompanyHotelCompetitorTagFromCompetitor(variables: {
		companyHotelId: number,
		competitorId: number,
		competitorTagId: number
	}) {
		const result = await GraphQLExecutor.execute<CompanyHotel>(REMOVE_COMPANY_HOTEL_COMPETITOR_TAG_FROM_COMPETITOR_MUTATION, variables);
		return new CompanyHotel(result);
	}

	public static async saveOrCreateCompanyHotelCompetitorsGroup(variables: {
		input: SaveOrCreateCompetitorsGroupInput
	}) {
		const result = await GraphQLExecutor.execute<CompanyHotel>(SAVE_OR_CREATE_COMPETITORS_GROUP_MUTATION, variables);
		return new CompanyHotel(result);
	}

	public static sanitizeRoomMappings(variables: { companyHotelId: number, hotelId: number }) {
		return GraphQLExecutor.execute<CompanyHotel>(SANITIZE_ROOM_MAPPINGS_MUTATION, variables);
	}

}


export default CompanyHotelService;
